import React, { useState, useRef, useEffect } from 'react';

/**
 * ExportMenu — Psychology-driven progressive disclosure
 *
 * Principles applied:
 * • Hick's Law: One button visible → fewer choices = faster decisions
 * • Gestalt proximity: Related actions grouped under clear section headers
 * • Progressive disclosure: Users only see options when they ask for them
 * • Visual hierarchy: Icons + section labels guide the eye naturally
 * • Affordance: ▾ chevron signals "there's more here"
 */
export function ExportMenu({ user, exportLoading, exportResumes, exportAll, handleImportExcel }) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);
  const fileRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const isLoading = !!exportLoading;

  const handleItem = (fn) => {
    setOpen(false);
    fn();
  };

  return (
    <div ref={menuRef} style={{ position: 'relative', display: 'inline-block' }}>
      {/* Trigger button — single CTA replaces 4 buttons */}
      <button
        className="btn-ghost"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          paddingRight: '10px',
          fontWeight: 600,
          letterSpacing: '0.2px',
          transition: 'all 0.15s',
          ...(open ? { background: 'var(--primary)', color: '#fff', borderColor: 'var(--primary)' } : {}),
        }}
        onClick={() => setOpen(v => !v)}
        disabled={isLoading}
        title="Export or import data"
        aria-haspopup="true"
        aria-expanded={open}
      >
        {isLoading ? (
          <>
            <span style={{ display: 'inline-block', width: 14, height: 14, border: '2px solid currentColor', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.6s linear infinite' }} />
            Working…
          </>
        ) : (
          <>
            <svg width="15" height="15" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
              <path d="M8 1v9M5 7l3 3 3-3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M2 12h12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
            </svg>
            Export / Import
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none" style={{ marginLeft: 2, transition: 'transform 0.2s', transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}>
              <path d="M2 3.5l3 3 3-3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </>
        )}
      </button>

      {/* Hidden file input — MUST be outside {open && ...} so it stays mounted when dropdown closes */}
      {user?.role === 'admin' && (
        <input
          ref={fileRef}
          type="file"
          accept=".xlsx,.xls,.csv"
          onChange={(e) => { handleImportExcel(e); e.target.value = ''; }}
          style={{ display: 'none' }}
        />
      )}

      {/* Dropdown panel */}
      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            right: 0,
            minWidth: 230,
            background: 'var(--bg2)',
            border: '1px solid var(--border)',
            borderRadius: 14,
            boxShadow: '0 16px 48px rgba(0,0,0,0.35), 0 4px 16px rgba(0,0,0,0.2)',
            zIndex: 200,
            overflow: 'hidden',
            animation: 'dropdownSlideIn 0.15s ease',
          }}
          role="menu"
        >
          {/* ── SECTION: DOWNLOAD ── */}
          <div style={{ padding: '10px 14px 4px', fontSize: '10px', fontWeight: 700, letterSpacing: '1px', color: 'var(--text3)', textTransform: 'uppercase' }}>
            Download
          </div>

          <MenuItem
            icon="📄"
            label="Download PDF"
            sublabel="All visible workers, formatted"
            loading={exportLoading === 'pdf'}
            onClick={() => handleItem(() => exportAll('pdf'))}
            primary
          />
          <MenuItem
            icon="📊"
            label="Download Excel"
            sublabel="Spreadsheet with all data"
            loading={exportLoading === 'excel'}
            onClick={() => handleItem(() => exportAll('excel'))}
          />
          <MenuItem
            icon="🗂"
            label="Download Resumes (.zip)"
            sublabel="Uploaded files + auto PDFs"
            loading={exportLoading === 'resumes'}
            onClick={() => handleItem(exportResumes)}
          />

          {/* ── DIVIDER ── */}
          {user?.role === 'admin' && (
            <div style={{ height: 1, background: 'var(--border)', margin: '6px 14px' }} />
          )}

          {/* ── SECTION: IMPORT (admin only) ── */}
          {user?.role === 'admin' && (
            <>
              <div style={{ padding: '4px 14px 4px', fontSize: '10px', fontWeight: 700, letterSpacing: '1px', color: 'var(--text3)', textTransform: 'uppercase' }}>
                Import
              </div>
              <MenuItem
                icon="⬆"
                label="Upload Excel"
                sublabel="Import workers from .xlsx / .csv"
                onClick={() => {
                  setOpen(false);
                  // Small delay so dropdown unmount doesn't interfere with click
                  setTimeout(() => fileRef.current?.click(), 50);
                }}
              />
            </>
          )}

          <div style={{ height: 8 }} />
        </div>
      )}

      <style>{`
        @keyframes dropdownSlideIn {
          from { opacity: 0; transform: translateY(-6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

function MenuItem({ icon, label, sublabel, loading, onClick, primary }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      role="menuitem"
      onClick={onClick}
      disabled={loading}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        width: '100%',
        padding: '9px 14px',
        background: hover ? 'rgba(59,130,246,0.08)' : 'transparent',
        border: 'none',
        cursor: loading ? 'wait' : 'pointer',
        textAlign: 'left',
        transition: 'background 0.12s',
        opacity: loading ? 0.6 : 1,
      }}
    >
      <span style={{ fontSize: 17, lineHeight: 1, flexShrink: 0 }}>{loading ? '⏳' : icon}</span>
      <div style={{ flex: 1 }}>
        <div style={{
          fontSize: 13,
          fontWeight: primary ? 700 : 600,
          color: hover ? 'var(--primary)' : 'var(--text)',
          lineHeight: 1.3,
        }}>
          {loading ? 'Working…' : label}
        </div>
        <div style={{ fontSize: 11, color: 'var(--text3)', lineHeight: 1.3, marginTop: 2 }}>
          {sublabel}
        </div>
      </div>
      {!loading && (
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ opacity: hover ? 0.8 : 0.25, flexShrink: 0, transition: 'opacity 0.12s' }}>
          <path d="M4 2l4 4-4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      )}
    </button>
  );
}

export default ExportMenu;